import React, { useState, useEffect } from 'react';
import { Form, Input, Button, message, Select, Col, Row, Spin} from 'antd';
import { loginRequest } from '../../authConfig';
import { useMsal } from '@azure/msal-react';
import axios from 'axios';
import TextArea from 'antd/es/input/TextArea';
import { set } from 'date-fns';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

interface ManageHelpDetailsProps {
  helpDetailsId?: string | null;
}

const ManageHelpDetails: React.FC<ManageHelpDetailsProps> = ({helpDetailsId}) => {
  const [items, setHelpCategoryData] = useState<[]>([]);
  const [helpCategoryId, setHelpCategoryId] = useState('');
  const [shortDescription, setShortDescription] = useState('');
  const [description, setDescription] = useState('');
  const [documentDescription, setDocumentDescription] = useState('');
  const [documentName, setDocumentName] = useState<File | null>(null);
  const [mediaDescription, setMediaDescription] = useState('');
  const [mediaName, setMediaName] = useState<File | null>(null);
  const [loading, setLoading] = useState<boolean>(false); // Loading state

  const { instance, accounts } = useMsal();

  const API_URL = process.env.REACT_APP_API_URL;

  const fetchToken = async () => {
        const res = await instance.acquireTokenSilent({
          ...loginRequest,
          account: accounts[0]
        });
        return res.idToken;
      };
    
    useEffect(() => {
        fetchToken();
        getHelpCategoryData();
    }, []);

    useEffect(() => {
        fetchToken();
        if (helpDetailsId != null) {
            loadHelpDetailsDataForEdit(helpDetailsId);
        }
    }, []);

    const getHelpCategoryData = async () => {
        try {
        const token = await fetchToken();
        const response = await axios.get(`${API_URL}/api/HelpDetails/GetHelpCategoryData`, {
            headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            }
        });
        setHelpCategoryData(response.data);
        } catch (error) {
        message.error('Failed to load help category data');
        }
    };

    const loadHelpDetailsDataForEdit = async (helpDetailsId: string) => {
        try {
          const token = await fetchToken();
          const response = await axios.get(`${API_URL}/api/HelpDetails/GetHelpDetailsById/${helpDetailsId}`, {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            }
          });
          if (response.status == 200) {
            console.log(response.data);
            setHelpCategoryId(response.data.helpCategoryId);
            setShortDescription(response.data.shortDescription);
            setDescription(response.data.description);
            setDocumentDescription(response.data.documentDescription);
            setDocumentName(response.data.documentName);
            setMediaDescription(response.data.mediaDescription);
            setMediaName(response.data.mediaName);
          }
        } catch (error) {
          console.error("Error fetching help details data", error);
        }
    };

    // File size limit (in bytes)
    const doc_max_file_size = 5 * 1024 * 1024; // 5 MB
    // Allowed file extensions
    const doc_allowed_extension = ['pdf', 'doc', 'docx', 'ppt', 'pptx'];

        // File size limit (in bytes)
    const media_max_file_size = 150 * 1024 * 1024; // 150 MB
    // Allowed file extensions
    const media_allowed_extension = ['mp4', 'avi', 'mov', 'mkv'];

  const handleSubmit = async () => {
    if (!helpCategoryId || !description || !shortDescription) {
      message.error('Please fill all fields');
      return;
    }   

    if (documentName && documentName.size > doc_max_file_size) {
        message.error('File size exceeds 5MB limit.');
        return;
    }
   
    const doc_file_extension = documentName?.name ? documentName.name.split('.').pop()?.toLowerCase() : '';
    if (documentName && typeof documentName !== 'string' && !doc_allowed_extension.includes(doc_file_extension || '')) {
        message.error('Invalid document file type. Only pdf, doc, docx, ppt, and pptx are allowed.');
        return;
    }

    if (documentName && !documentDescription) {
        message.error('Please enter document name.');
        return;
    }

    if (mediaName && mediaName.size > media_max_file_size) {
        message.error('File size exceeds 150MB limit.');
        return;
    }

    const media_file_extension = mediaName?.name ? mediaName.name.split('.').pop()?.toLowerCase() : '';
    if (mediaName && typeof mediaName !== 'string' && !media_allowed_extension.includes(media_file_extension || '')) {
        message.error('Invalid media file type. Only mp4, avi, mov, and mkv are allowed.');
        return;
    }

    if (mediaName && !mediaDescription) {
        message.error('Please enter media name.');
        return;
    }
    
    setLoading(true); // Start loading

    const userId = localStorage.getItem("UserId");

    const formData = new FormData();
    formData.append('HelpCategoryId', helpCategoryId);
    formData.append('Description', description);
    formData.append('ShortDescription', shortDescription);
    formData.append('DocumentDescription', documentDescription && documentDescription.trim() !== '' ? documentDescription : '');
    if (documentName) {
      formData.append('DocumentUrl', documentName);
      formData.append('DocumentName', documentName);
    }
    else {
    formData.append('DocumentUrl', '');
    }

    formData.append('MediaDescription', mediaDescription && mediaDescription.trim() !== '' ? mediaDescription : '');
    if (mediaName) {
      formData.append('MediaUrl', mediaName);
      formData.append('MediaName', mediaName);
    }
    else {
      formData.append('MediaUrl', '');
    }
    
    try {
        const token = await fetchToken();
        if(helpDetailsId != null) {
            //Update the data
            formData.append('HelpDetailsId', helpDetailsId || '');
            formData.append('UpdatedBy', userId || '');
            await axios.patch(`${API_URL}/api/HelpDetails/UpdateHelpDetailsData`, formData,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });
            message.success('Help Details updated successfully');
        }
        else{
          //Add the data
          formData.append('CreatedBy', userId || '');
          await axios.post(`${API_URL}/api/HelpDetails/SaveHelpDetailsData`, formData,
          {
              headers: {
                  Authorization: `Bearer ${token}`,
                  'Content-Type': 'multipart/form-data'
              }
          });
          message.success('Help Details added successfully');
        }
        setHelpCategoryId('');
        setDescription('');
        setDocumentDescription('');
        setMediaDescription('');
        setShortDescription('');
        setDocumentName(null);
        setMediaName(null);
    } catch (error) {
        message.error('Failed to save the data');
    }
    finally {
      setLoading(false); // Stop loading
    }
  };

    const handleDocumentUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files ? event.target.files[0] : null;
      if (file) {
        setDocumentName(file);
      }
    }

    const handleMediaFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files ? event.target.files[0] : null;
      if (file) {
        setMediaName(file);
      }
    };

    const handleDescriptionChange = (value: string) => {
      // Remove HTML tags to count only text characters
      const plainText = value.replace(/<[^>]+>/g, '');
      if (plainText.length <= 5000) {
        setDescription(value);
      }
    };

  return (
    <div>
      <Spin spinning={loading} tip="Submitting...">
      <div>
          <Form layout="vertical" onFinish={handleSubmit}>
            <Row gutter={16}>
                <Col span={12}>
                    <Form.Item label="Help Category Name" required>
                        <Select
                            value={helpCategoryId || undefined}
                            placeholder="Select Help Category"
                            onChange={(value) => setHelpCategoryId(value)}
                            allowClear
                        >
                            {items.map((type: any) => (
                                <Select.Option key={type.value} value={type.value}>
                                    {type.text}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item label="Short Description" required>
                        <Input
                            value={shortDescription}
                            onChange={(e) => setShortDescription(e.target.value)}
                            placeholder="Enter Short Description" maxLength={300}
                        />
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={16}>
                <Col span={12}>
                    <Form.Item label="Document Name">
                        <Input
                            value={documentDescription}
                            onChange={(e) => setDocumentDescription(e.target.value)}
                            placeholder="Enter Document Name" maxLength={50}
                        />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item label="Upload Document">
                        <Input type='file' accept=".pdf,.doc,.docx,.ppt,.pptx" onChange={handleDocumentUpload}></Input>
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={16}>
                <Col span={12}>
                    <Form.Item label="Media Name">
                        <Input
                            value={mediaDescription}
                            onChange={(e) => setMediaDescription(e.target.value)}
                            placeholder="Enter Media Name" maxLength={50}
                        />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item label="Upload Video File">
                        <Input type='file' accept=".mp4,.avi,.mov,.mkv" onChange={handleMediaFileUpload}></Input>
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={16}>
                <Col span={24}>
                    <Form.Item label="Description" required>
                      <ReactQuill
                        value={description}
                        placeholder="Enter Description"
                        onChange={handleDescriptionChange}
                        style={{ height: 150 }}
                      />
                        <div style={{ textAlign: "right", color: "#888", fontSize: 12, paddingRight:"10px" }}>
                          {description.replace(/<[^>]+>/g, '').length}/5000 characters
                        </div>
                    </Form.Item>
                </Col>
            </Row>
          <Form.Item>
            <Button type="primary" htmlType="submit" style={{marginTop:"25px"}}>
              Save
            </Button>
          </Form.Item>
        </Form>
      </div>
      </Spin>
    </div>
    
  );
};

export default ManageHelpDetails;